import type { paths } from "@src/packages/openapi/schema";

/**
 * One Gardner scale for every place that turns a grade into a stage or a stage
 * back into a grade: the cryo vault intake writes through it, and the embryo
 * testing sheet and the cryo vault read through it, so a grade she saves reads
 * back as the grade she entered.
 */

type EmbryoDayDetail = NonNullable<
  Extract<
    NonNullable<
      paths["/{tenantId}/patients/{patientId}/specimens"]["get"]["responses"][200]["content"]["application/json"]["data"]
    >[number],
    { type: "embryo" }
  >["dayDetails"]
>[number];

export type EmbryoStage = NonNullable<EmbryoDayDetail["stage"]>;
type GardnerLetter = NonNullable<EmbryoDayDetail["icmGrade"]>;

export type EmbryoGradingObservation = Partial<
  Pick<
    EmbryoDayDetail,
    | "stage"
    | "gradingNumerical"
    | "gradingAlphabetical"
    | "icmGrade"
    | "teGrade"
    | "source"
  >
>;

/** The day-observation fields a Gardner grade such as "4AA" is saved as. */
export type GardnerDayDetail = {
  stage: EmbryoStage;
  gradingNumerical: number;
  icmGrade: GardnerLetter;
  teGrade: GardnerLetter;
};

/**
 * The Gardner expansion scale: expansion 1 is an early blastocyst, 2 a
 * blastocyst, 3 a full blastocyst, 4 an expanded blastocyst, 5 a hatching
 * blastocyst and 6 a hatched blastocyst. Expansion `n` is entry `n - 1`.
 */
const GARDNER_STAGES: readonly EmbryoStage[] = [
  "early_blastocyst",
  "blastocyst",
  "full_blastocyst",
  "expanded_blastocyst",
  "hatching_blastocyst",
  "fully_hatched_blastocyst",
];

const GARDNER_LETTERS: readonly GardnerLetter[] = ["A", "B", "C"];

export function gardnerStageForExpansion(
  expansion: number,
): EmbryoStage | null {
  if (!Number.isInteger(expansion)) return null;
  return GARDNER_STAGES[expansion - 1] ?? null;
}

function gardnerExpansionForStage(stage: EmbryoStage): number | null {
  const index = GARDNER_STAGES.indexOf(stage);
  return index === -1 ? null : index + 1;
}

function gardnerLetter(value: string): GardnerLetter | null {
  const upper = value.toUpperCase();
  return GARDNER_LETTERS.find((letter) => letter === upper) ?? null;
}

/**
 * Reads a Gardner grade the way a lab writes it: the expansion (1 to 6), then
 * the inner cell mass and trophectoderm grades (A to C), e.g. "4AA". The
 * expansion is kept as recorded alongside its stage, so the grade reads back
 * exactly as entered. Anything else is not a Gardner grade.
 */
export function parseGardnerGrade(grade: string): GardnerDayDetail | null {
  const match = /^([1-6])([A-C])([A-C])$/i.exec(grade);
  if (!match) return null;

  const expansion = Number(match[1]);
  const stage = gardnerStageForExpansion(expansion);
  const icmGrade = gardnerLetter(match[2] ?? "");
  const teGrade = gardnerLetter(match[3] ?? "");
  if (!stage || !icmGrade || !teGrade) return null;

  return { stage, gradingNumerical: expansion, icmGrade, teGrade };
}

/**
 * Until the intake moved onto the scale above it saved every Gardner grade one
 * stage too far along: "3" as blastocyst, "4" as full, "5" as expanded and "6"
 * as hatching, with "1" and "2" both as early blastocyst. It saved the inner
 * cell mass and trophectoderm but never the expansion number. The intake now
 * always records the expansion, so a blastocyst observation she or her partner
 * entered with inner cell mass and trophectoderm grades and no recorded grade
 * can only have come from the old intake. Its stage reads back through the old
 * scale; "1" and "2" cannot be told apart and read as "2", the lowest grade the
 * intake offered. Observations from the clinic are never reread.
 */
const OLD_INTAKE_EXPANSION: Partial<Record<EmbryoStage, number>> = {
  early_blastocyst: 2,
  blastocyst: 3,
  full_blastocyst: 4,
  expanded_blastocyst: 5,
  hatching_blastocyst: 6,
};

function oldIntakeExpansion(
  observation: EmbryoGradingObservation,
): number | null {
  const {
    stage,
    source,
    gradingNumerical,
    gradingAlphabetical,
    icmGrade,
    teGrade,
  } = observation;
  if (source !== "patient" && source !== "partner") return null;
  if (gradingNumerical != null || gradingAlphabetical != null) return null;
  if (!stage || !icmGrade || !teGrade) return null;
  return OLD_INTAKE_EXPANSION[stage] ?? null;
}

/**
 * The stage an observation describes. This is the recorded stage, except for
 * an observation the old intake saved one stage too far along.
 */
export function readEmbryoStage(
  observation: EmbryoGradingObservation,
): EmbryoStage | null {
  const oldExpansion = oldIntakeExpansion(observation);
  if (oldExpansion != null) return gardnerStageForExpansion(oldExpansion);
  return observation.stage ?? null;
}

function gardnerExpansion(
  observation: EmbryoGradingObservation,
): number | null {
  if (observation.gradingNumerical != null) return observation.gradingNumerical;
  const oldExpansion = oldIntakeExpansion(observation);
  if (oldExpansion != null) return oldExpansion;
  return observation.stage ? gardnerExpansionForStage(observation.stage) : null;
}

/**
 * A blastocyst reads as Gardner notation (expansion, inner cell mass,
 * trophectoderm, e.g. "4AA"). The expansion comes from the recorded number
 * when there is one, otherwise from the stage. Earlier embryos read as the
 * grade the lab recorded.
 */
export function formatEmbryoGrade(
  observation: EmbryoGradingObservation,
): string | null {
  const { gradingNumerical, gradingAlphabetical, icmGrade, teGrade } =
    observation;

  if (icmGrade && teGrade) {
    const expansion = gardnerExpansion(observation);
    return `${expansion ?? ""}${icmGrade}${teGrade}`;
  }
  if (gradingNumerical != null && gradingAlphabetical) {
    return `${gradingNumerical}${gradingAlphabetical}`;
  }
  if (gradingAlphabetical) return gradingAlphabetical;
  if (gradingNumerical != null) return String(gradingNumerical);
  return null;
}
